/**
 * Value Chain Permissions API
 * 
 * This module provides API calls for managing value chain permissions.
 * It allows users to view their accessible value chains and request access to new ones.
 */

import apiClient from './client';

// Types
interface ValueChainAccess {
  id: number;
  value_chain_id: string;
  value_chain_name: string;
  permission_level: string;
  is_active: boolean;
  granted_at: string;
  expires_at?: string;
  granted_by?: number;
  notes?: string;
}

interface ValueChainWithPermission {
  element_id: string;
  name: string;
  has_access: boolean;
  permission_level?: string;
  is_active?: boolean;
  granted_at?: string;
  expires_at?: string;
}

interface GrantAccessRequest {
  value_chain_id: string;
  value_chain_name: string;
  permission_level?: string;
  notes?: string;
}

interface GrantAccessResponse {
  success: boolean;
  message: string;
  permission_id?: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  details?: any;
}

class ValueChainPermissionsAPI {
  private client: typeof apiClient;

  constructor() {
    this.client = apiClient;
  }

  /**
   * Get value chains that the current user has access to
   */
  async getAccessibleValueChains(): Promise<ApiResponse<ValueChainAccess[]>> {
    try {
      const response = await this.client.authenticatedGet('/api/v1/auth/value-chains/accessible');
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Error fetching accessible value chains:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch accessible value chains',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get all value chains with permission status for the current user
   */
  async getAllValueChainsWithPermissions(): Promise<ApiResponse<ValueChainWithPermission[]>> {
    try {
      const response = await this.client.authenticatedGet('/api/v1/auth/value-chains/all');
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Error fetching value chains with permissions:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch value chains with permissions',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Grant access to a specific value chain
   */
  async grantAccess(request: GrantAccessRequest): Promise<ApiResponse<GrantAccessResponse>> {
    try {
      const response = await this.client.authenticatedPost('/api/v1/auth/value-chains/grant', {
        value_chain_id: request.value_chain_id,
        value_chain_name: request.value_chain_name,
        permission_level: request.permission_level || 'read',
        notes: request.notes,
      });
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Error granting value chain access:', error);
      return {
        success: false,
        error: error.message || 'Failed to grant value chain access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Revoke access to a specific value chain
   */
  async revokeAccess(permissionId: number): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await this.client.authenticatedDelete(`/api/v1/auth/value-chains/${permissionId}`);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Error revoking value chain access:', error);
      return {
        success: false,
        error: error.message || 'Failed to revoke value chain access',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Update value chain permission
   */
  async updatePermission(permissionId: number, updateData: {
    permission_level?: string;
    is_active?: boolean;
    expires_at?: string;
    notes?: string;
  }): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await this.client.authenticatedPut(`/api/v1/auth/value-chains/${permissionId}`, updateData);
      const data = await response.json();
      
      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Error updating value chain permission:', error);
      return {
        success: false,
        error: error.message || 'Failed to update value chain permission',
        details: error.response ? await this.getErrorDetails(error.response) : null,
      };
    }
  }

  /**
   * Get error details from response
   */
  private async getErrorDetails(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return { status: response.status, statusText: response.statusText };
    }
  }
}

// Create and export singleton instance
const valueChainPermissionsAPI = new ValueChainPermissionsAPI();
export default valueChainPermissionsAPI;

